<script setup lang="ts">
import { computed, toRef } from "vue";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
import { Select } from "@/components/episto-ui/select";
import MultipleChoiceQuestionField from "./CardOptionFields/MultipleChoiceQuestionField.vue";
import PopulationField from "./CardOptionFields/PopulationField.vue";
import { useCardLocalOptions } from "@/composables/useCardLocalOptions";
import type { ChartCard } from "@/types";
import { cardCodes, isMultipleChoiceCard, useAnalysisContext, useCardPopulation } from "./analysisData";
import ColorsField from "./CardOptionFields/ColorsField.vue";
import ColorToggleField from "./CardOptionFields/ColorToggleField.vue";
import OptionRow from "./CardOptionFields/OptionRow.vue";
import OptionSection from "./CardOptionFields/OptionSection.vue";

const props = defineProps({
  card: { type: Object, required: true },
});

const card = toRef(props, "card");
const cardsStore = useResultsV2CardsStore();
const { availableCards, displayLocale } = useAnalysisContext();
const { updateLocalOption } = useCardLocalOptions(card);
const { color: populationColor } = useCardPopulation(card);

const resolveTitle = (title: unknown) => removeHtmlMarkdownAndLineBreak(resolveLocalizedTitle(title, displayLocale.value));

function setContent(patch: Record<string, unknown>) {
  cardsStore.updateCardLocal(props.card.id, { content: { ...props.card.content, ...patch } });
}

const questionId = computed<number | null>(() => props.card.content?.question_id || null);

const questionMeta = computed<ChartCard | null>(
  () =>
    (availableCards.value as ChartCard[]).find((c) => isMultipleChoiceCard(c) && (c.questions || []).some((q) => q.id === questionId.value)) ||
    null,
);

const answerOptions = computed(() =>
  questionMeta.value ? cardCodes(questionMeta.value, resolveTitle).map(({ code, label }) => ({ value: code, label })) : [],
);

const codes = computed({
  get: () => props.card.content?.codes || [],
  set: (value: string[] | null) => setContent({ codes: value || [] }),
});

const isMatrix = computed(() => codes.value.length > 3);

const matrixColor = computed({
  get: () => props.card.options?.color || null,
  set: (value: string | null) => updateLocalOption("color", value),
});
</script>

<template>
  <OptionSection
    id="data"
    title="Data">
    <MultipleChoiceQuestionField
      :card="card"
      :content-on-pick="() => ({ codes: [] })" />

    <OptionRow
      v-if="answerOptions.length"
      label="Answers to compare"
      hint="Two or three answers draw a proportional Venn diagram; four or more switch to the duplication matrix.">
      <Select
        :model-value="codes"
        :options="answerOptions"
        placeholder="Pick at least 2 answers..."
        multiple
        filterable
        @update:model-value="codes = $event" />
    </OptionRow>

    <PopulationField :card="card" />
  </OptionSection>

  <OptionSection
    v-if="questionMeta"
    id="display"
    title="Display"
    :count="isMatrix && matrixColor ? 1 : 0">
    <ColorToggleField
      v-if="isMatrix"
      v-model="matrixColor"
      :default-color="populationColor"
      label="Matrix color" />
    <ColorsField
      v-else
      :card="card"
      :card-meta="questionMeta"
      :display-locale="displayLocale" />
  </OptionSection>
</template>
